import { useState } from 'react'
import { useGameActions } from '../../hooks/useGameActions'
import { useGameStore } from '../../store/gameStore'
import { GameShell } from '../game/GameShell'
import { MobileChatModal } from './MobileChatModal'
import { MobileChatPreview } from './MobileChatPreview'
import { MobileEvolutionsPanel } from './MobileEvolutionsPanel'
import { MobileFlowerMarketPanel } from './MobileFlowerMarketPanel'
import { MobilePlayersOverlay } from './MobilePlayersOverlay'
import { MobilePollinatePanel } from './MobilePollinatePanel'
import { MobileTopBar } from './MobileTopBar'

type MobilePanel = 'flowers' | 'pollinate' | 'evolutions' | 'chat' | null

export function MobileGameShell() {
  const {
    room,
    member,
    memberId,
    availableActions,
    setShowDonationModal,
    setShowGameMenu,
  } = useGameStore()
  const { pass, forage, pollinate, evolve } = useGameActions()
  const [activePanel, setActivePanel] = useState<MobilePanel>(null)
  const [boardZoom, setBoardZoom] = useState(2.5)

  if (!room?.game || !member?.color) {
    return <GameShell shellMode="mobile" boardZoom={boardZoom} />
  }

  const game = room.game
  const isCurrentTurn = game.current_player.color === member.color
  const togglePanel = (panel: Exclude<MobilePanel, null>) => {
    setActivePanel((currentPanel) => (currentPanel === panel ? null : panel))
  }

  return (
    <div id="mobile-shell-game" className="mobile-shell-game">
      <GameShell shellMode="mobile" boardZoom={boardZoom} />
      <div
        id="mobile-panel-backdrop"
        className={`mobile-panel-backdrop${activePanel ? ' visible' : ''}`}
        onClick={() => setActivePanel(null)}
      />
      <div className="mobile-overlay-layer">
        <MobileTopBar
          game={game}
          isCurrentTurn={isCurrentTurn}
          onPass={() => {
            void pass()
            setActivePanel(null)
          }}
          onOpenFlowers={() => togglePanel('flowers')}
          onOpenPollinate={() => togglePanel('pollinate')}
          onOpenDonate={() => {
            setActivePanel(null)
            setShowDonationModal(true)
          }}
          onOpenEvolutions={() => togglePanel('evolutions')}
          onOpenMenu={() => {
            setActivePanel(null)
            setShowGameMenu(true)
          }}
        />
        <MobilePlayersOverlay
          game={game}
          member={member}
          members={room.members}
          leftMembers={room.left_members}
        />
        <div className="mobile-chat-stack">
          <div className="mobile-board-zoom-panel">
            <div className="mobile-board-zoom-label">Hive Zoom</div>
            <input
              id="mobile-board-zoom"
              className="mobile-board-zoom-range"
              type="range"
              min={1}
              max={2.5}
              step={0.05}
              value={boardZoom}
              onChange={(event) => setBoardZoom(Number(event.target.value))}
            />
          </div>
          <MobileChatPreview
            room={room}
            game={game}
            memberId={memberId}
            activePanel={activePanel}
            onOpenChat={() => togglePanel('chat')}
          />
        </div>
      </div>
      <MobileFlowerMarketPanel
        game={game}
        availableActions={availableActions}
        activePanel={activePanel}
        onForage={(flowerId) => {
          void forage(flowerId)
          setActivePanel(null)
        }}
        onClose={() => setActivePanel(null)}
      />
      <MobilePollinatePanel
        game={game}
        member={member}
        availableActions={availableActions}
        activePanel={activePanel}
        onPollinate={(flowerId) => {
          void pollinate(flowerId)
          setActivePanel(null)
        }}
        onClose={() => setActivePanel(null)}
      />
      <MobileEvolutionsPanel
        game={game}
        member={member}
        availableActions={availableActions}
        activePanel={activePanel}
        onEvolve={(evolutionId) => {
          void evolve(evolutionId)
          setActivePanel(null)
        }}
        onClose={() => setActivePanel(null)}
      />
      <MobileChatModal
        room={room}
        game={game}
        memberId={memberId}
        activePanel={activePanel}
        onClose={() => setActivePanel(null)}
      />
    </div>
  )
}
